import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getIdTokenResult,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import { AdminAuthAPI } from "../api/adminAuth";
import { setHttpAuthToken } from "../api/http";
import { auth as firebaseAuth } from "../firebase";
import { AdminUser } from "../types/adminAuth";

type AdminAuthContextValue = {
  token: string | null;
  user: AdminUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  canManageContent: boolean;
  isSuperAdmin: boolean;
  firebaseReady: boolean;
  firebaseUid: string | null;
  firebaseError: string | null;
  login: (email: string, password: string) => Promise<AdminUser>;
  bootstrap: (
    fullName: string,
    email: string,
    password: string
  ) => Promise<AdminUser>;
  logout: () => void;
  refreshMe: () => Promise<AdminUser | null>;
};

const STORAGE_KEY = "cds_admin_session_v1";

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(
  undefined
);

function getErrorMessage(error: unknown, fallback: string): string {
  const responseMessage = (error as any)?.response?.data?.message;
  const directMessage = (error as any)?.message;
  const message = String(responseMessage || directMessage || fallback).trim();
  return message || fallback;
}

function readStoredSession(): { token: string; user: AdminUser } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = String(parsed?.token || "").trim();
    const user = parsed?.user as AdminUser | undefined;
    if (!token || !user?.id || !user?.email) return null;
    return { token, user };
  } catch {
    return null;
  }
}

function writeStoredSession(token: string | null, user: AdminUser | null) {
  if (typeof window === "undefined") return;
  if (!token || !user) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const initialSession = readStoredSession();
  const [token, setToken] = useState<string | null>(initialSession?.token || null);
  const [user, setUser] = useState<AdminUser | null>(initialSession?.user || null);
  const [loading, setLoading] = useState(Boolean(initialSession?.token));
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [firebaseUid, setFirebaseUid] = useState<string | null>(
    firebaseAuth.currentUser?.uid || null
  );
  const [firebaseError, setFirebaseError] = useState<string | null>(null);

  const clearSession = useCallback(() => {
    setToken(null);
    setUser(null);
    setHttpAuthToken(null);
    writeStoredSession(null, null);
  }, []);

  const setSession = useCallback((nextToken: string, nextUser: AdminUser) => {
    const normalizedToken = String(nextToken || "").trim();
    setToken(normalizedToken);
    setUser(nextUser);
    setHttpAuthToken(normalizedToken);
    writeStoredSession(normalizedToken, nextUser);
  }, []);

  useEffect(() => {
    setHttpAuthToken(token);
  }, [token]);

  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, (firebaseUser) => {
      setFirebaseUid(firebaseUser?.uid ?? null);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    let ignore = false;
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const { user: freshUser } = await AdminAuthAPI.me();
        if (ignore) return;
        setSession(token, freshUser);
      } catch {
        if (ignore) return;
        clearSession();
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [clearSession, setSession, token]);

  useEffect(() => {
    let ignore = false;

    if (!token) {
      setFirebaseReady(false);
      setFirebaseError(null);
      void signOut(firebaseAuth).catch(() => {});
      return;
    }

    setFirebaseReady(false);
    setFirebaseError(null);

    (async () => {
      try {
        const { firebaseToken } = await AdminAuthAPI.firebaseToken();
        const credential = await signInWithCustomToken(firebaseAuth, firebaseToken);
        const tokenResult = await getIdTokenResult(credential.user, true);

        if (tokenResult.claims.admin !== true) {
          throw new Error("Firebase session is missing the required admin claim.");
        }

        if (ignore) return;
        setFirebaseReady(true);
        setFirebaseUid(credential.user.uid);
      } catch (error) {
        if (ignore) return;
        setFirebaseReady(false);
        setFirebaseError(
          getErrorMessage(
            error,
            "Failed to connect the admin panel to Firebase support chat."
          )
        );
        void signOut(firebaseAuth).catch(() => {});
      }
    })();

    return () => {
      ignore = true;
    };
  }, [token]);

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await AdminAuthAPI.login({ email, password });
      setSession(data.token, data.user);
      return data.user;
    },
    [setSession]
  );

  const bootstrap = useCallback(
    async (fullName: string, email: string, password: string) => {
      const data = await AdminAuthAPI.bootstrap({ fullName, email, password });
      setSession(data.token, data.user);
      return data.user;
    },
    [setSession]
  );

  const refreshMe = useCallback(async () => {
    if (!token) return null;
    const { user: freshUser } = await AdminAuthAPI.me();
    setSession(token, freshUser);
    return freshUser;
  }, [setSession, token]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      token,
      user,
      loading,
      isAuthenticated: Boolean(token && user && user.isActive),
      canManageContent: user?.role === "super_admin",
      isSuperAdmin: user?.role === "super_admin",
      firebaseReady,
      firebaseUid,
      firebaseError,
      login,
      bootstrap,
      logout,
      refreshMe,
    }),
    [
      bootstrap,
      firebaseError,
      firebaseReady,
      firebaseUid,
      loading,
      login,
      logout,
      refreshMe,
      token,
      user,
    ]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  }
  return ctx;
}
